import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../lib/api';

export default function Login() {
  const { login }    = useAuth();
  const navigate     = useNavigate();
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    setError('');
    try {
      const result = await authApi.login(email, password);
      login(result.token, result.user);
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-wrap" style={{ gridTemplateColumns: '60% 40%' }}>

      {/* ── Left aside ── */}
      <div className="login-aside">
        {/* Brand */}
        <div className="row" style={{ gap: 12 }}>
          <img src="/logo.png" alt="PsicoDosis" style={{ width: 46, height: 46 }} />
          <div>
            <div style={{ fontFamily: 'var(--serif)', fontSize: 23, fontWeight: 600, lineHeight: 1 }}>
              Psico<span style={{ color: 'var(--copper)' }}>Dosis</span>
            </div>
            <div style={{ fontSize: 10.5, letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--ink-2)', marginTop: 4 }}>
              Panel interno
            </div>
          </div>
        </div>

        {/* Headline + body */}
        <div style={{ maxWidth: 430 }}>
          <div style={{ fontFamily: 'var(--serif)', fontSize: '2.6rem', lineHeight: 1.15, letterSpacing: '-0.01em', color: '#3a342b' }}>
            Una dosis de psicología, cada día.
          </div>
          <p style={{ fontSize: 17, color: 'var(--ink-2)', marginTop: 18, lineHeight: 1.6, maxWidth: 400 }}>
            Desde aquí generas temas, creas contenido con IA, apruebas piezas, publicas y cuidas tu comunidad. Todo en un solo lugar, sin tocar código.
          </p>
          <p style={{ fontSize: 17, color: 'var(--ink-2)', marginTop: 22, lineHeight: 1.6, maxWidth: 400, fontStyle: 'italic' }}>
            Conecta con quienes más lo necesitan,<br />un contenido a la vez.
          </p>
        </div>

        {/* Decorative logo bg */}
        <div aria-hidden style={{ position: 'absolute', right: -100, top: -120, width: 620, height: 620, opacity: 0.13, pointerEvents: 'none' }}>
          <img src="/logo.png" alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        </div>
      </div>

      {/* ── Right form ── */}
      <div className="login-form-side" style={{ padding: '40px 16px' }}>
        <form className="login-card" style={{ width: '100%', maxWidth: 380 }} onSubmit={handleSubmit}>
          <h1 style={{ fontFamily: 'var(--serif)', fontSize: 26, margin: '0 0 6px' }}>Bienvenida de nuevo</h1>
          <p className="muted" style={{ margin: '0 0 26px', fontSize: 13.5 }}>Ingresa para administrar PsicoDosis.</p>

          {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}

          <label className="field-label">Correo</label>
          <div style={{ position: 'relative', marginBottom: 16 }}>
            <span style={{ position: 'absolute', left: 13, top: 13, color: 'var(--muted)', pointerEvents: 'none' }}>
              <MailIco size={17} />
            </span>
            <input className="input" type="email" style={{ paddingLeft: 40 }}
              placeholder="admin@pzicodosis.com"
              value={email} onChange={e => setEmail(e.target.value)}
              autoComplete="email" required />
          </div>

          <label className="field-label">Contraseña</label>
          <div style={{ position: 'relative', marginBottom: 10 }}>
            <span style={{ position: 'absolute', left: 13, top: 13, color: 'var(--muted)', pointerEvents: 'none' }}>
              <LockIco size={17} />
            </span>
            <input className="input" type="password" style={{ paddingLeft: 40 }}
              placeholder="••••••••"
              value={password} onChange={e => setPassword(e.target.value)}
              autoComplete="current-password" required />
          </div>

          <div className="row" style={{ justifyContent: 'space-between', marginBottom: 22 }}>
            <label className="row muted" style={{ gap: 7, fontSize: 13, cursor: 'pointer' }}>
              <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)}
                style={{ accentColor: 'var(--accent)', width: 15, height: 15 }} />
              Recordarme
            </label>
            <a href="#" onClick={e => e.preventDefault()}
              style={{ fontSize: 13, color: 'var(--accent-ink)', textDecoration: 'none', fontWeight: 600 }}>
              ¿Olvidaste tu clave?
            </a>
          </div>

          <button type="submit" className="btn btn-primary input-lg"
            style={{ width: '100%', justifyContent: 'center', fontSize: 14.5 }}
            disabled={loading}>
            {loading ? <RefreshIco size={18} /> : <ArrowRIco size={18} />}
            {loading ? 'Ingresando…' : 'Ingresar al panel'}
          </button>
          <p className="muted" style={{ fontSize: 12, textAlign: 'center', marginTop: 18 }}>
            Acceso exclusivo del administrador · PsicoDosis © 2026
          </p>
        </form>
      </div>
    </div>
  );
}

/* ── Icons ── */
function Svg({ children, size = 18 }: { children: React.ReactNode; size?: number }) {
  return <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">{children}</svg>;
}
function MailIco({ size = 18 }: { size?: number })    { return <Svg size={size}><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M2 7l10 7 10-7"/></Svg>; }
function LockIco({ size = 18 }: { size?: number })    { return <Svg size={size}><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></Svg>; }
function RefreshIco({ size = 18 }: { size?: number }) { return <Svg size={size}><path d="M3 12a9 9 0 0 1 15.5-6.3M21 4v4h-4"/><path d="M21 12a9 9 0 0 1-15.5 6.3M3 20v-4h4"/></Svg>; }
function ArrowRIco({ size = 18 }: { size?: number })  { return <Svg size={size}><path d="M5 12h14M13 6l6 6-6 6"/></Svg>; }
